import React, { useState } from 'react';
import VehicleSelector from './components/VehicleSelector.jsx';
import SceneSelector from './components/SceneSelector.jsx';
import SeasonSelector from './components/SeasonSelector.jsx';
import PromotionSelector from './components/PromotionSelector.jsx';
import StyleSelector from './components/StyleSelector.jsx';
import AspectRatioSelector from './components/AspectRatioSelector.jsx';
import TextOverlayEditor from './components/TextOverlayEditor.jsx';
import CustomOptions from './components/CustomOptions.jsx';
import ImagePreview from './components/ImagePreview.jsx';
import GenerateButton from './components/GenerateButton.jsx';
import { imageGenerator } from './services/imageGenerator.js';
import SafeIcon from './common/SafeIcon.jsx';
import * as FiIcons from 'react-icons/fi';

const { FiCar, FiSettings } = FiIcons;

function App() {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [selectedScene, setSelectedScene] = useState('showroom');
  const [selectedSeason, setSelectedSeason] = useState('summer');
  const [selectedPromotion, setSelectedPromotion] = useState('none');
  const [selectedStyle, setSelectedStyle] = useState('photorealistic');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState('landscape');
  const [dealershipName, setDealershipName] = useState('');
  const [customText, setCustomText] = useState('');
  const [colors, setColors] = useState([]);
  const [textOverlays, setTextOverlays] = useState([]);
  const [imageResult, setImageResult] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState('');

  const handleGenerate = async () => {
    if (!selectedVehicle) {
      alert('Please select a vehicle first!');
      return;
    }

    setIsGenerating(true);
    setImageResult(null);

    const config = {
      vehicle: selectedVehicle,
      scene: selectedScene,
      season: selectedSeason,
      promotion: selectedPromotion,
      style: selectedStyle,
      aspectRatio: selectedAspectRatio,
      customText,
      dealershipName,
      colors,
      textOverlays
    };

    const prompt = imageGenerator.buildPrompt(config);
    setGeneratedPrompt(prompt);

    try {
      const result = await imageGenerator.generateImage(prompt, selectedAspectRatio);
      setImageResult(result);
    } catch (error) {
      setImageResult({
        success: false,
        error: error.message,
        prompt: prompt
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const canGenerate = selectedVehicle.trim() !== '';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-3">
            <SafeIcon icon={FiCar} className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Shawn Ryder Digitall - Vehicle Marketing Generator
              </h1>
              <p className="text-gray-600 mt-1">
                Create stunning marketing images with custom text overlays and offers
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Configuration Panel */}
          <div className="lg:col-span-2 space-y-8">
            {/* Vehicle Selection */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <VehicleSelector
                selectedVehicle={selectedVehicle}
                onVehicleChange={setSelectedVehicle}
              />
            </div>

            {/* Scene Selection */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <SceneSelector
                selectedScene={selectedScene}
                onSceneChange={setSelectedScene}
              />
            </div>

            {/* Image Format & Size */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <AspectRatioSelector
                selectedAspectRatio={selectedAspectRatio}
                onAspectRatioChange={setSelectedAspectRatio}
              />
            </div>

            {/* Text Overlays & Offers */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <TextOverlayEditor
                textOverlays={textOverlays}
                onTextOverlaysChange={setTextOverlays}
              />
            </div>

            {/* Season and Promotion */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <SeasonSelector
                  selectedSeason={selectedSeason}
                  onSeasonChange={setSelectedSeason}
                />
              </div>

              <div className="bg-white rounded-lg shadow-lg p-6">
                <PromotionSelector
                  selectedPromotion={selectedPromotion}
                  onPromotionChange={setSelectedPromotion}
                />
              </div>
            </div>

            {/* Style Selection */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <StyleSelector
                selectedStyle={selectedStyle}
                onStyleChange={setSelectedStyle}
              />
            </div>

            {/* Custom Options */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <CustomOptions
                dealershipName={dealershipName}
                onDealershipNameChange={setDealershipName}
                customText={customText}
                onCustomTextChange={setCustomText}
                colors={colors}
                onColorsChange={setColors}
              />
            </div>

            {/* Generate Button */}
            <div className="bg-white rounded-lg shadow-lg p-6">
              <GenerateButton
                onGenerate={handleGenerate}
                isGenerating={isGenerating}
                disabled={!canGenerate}
              />
            </div>
          </div>

          {/* Preview Panel */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <ImagePreview
                imageResult={imageResult}
                isGenerating={isGenerating}
                prompt={generatedPrompt}
                aspectRatio={selectedAspectRatio}
                textOverlays={textOverlays}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;